import React, { useCallback, useEffect, useMemo, useState } from "react";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";

import CommonCard from "../../components/common_card";
import { TypographyTitle, TypographyBold } from "../../style/typography";
import ReportTimeline from "../../components/arrowbox";
import DamReportStepper from "../../components/dam_report_stepper";
import MailConfirm from "../../components/mail_confirm";
import ApprovalStatusInfo from "../../components/approval_status_info";
import ReportCountdown from "../../components/report_countdown";
import ReportStopDialog from "../../components/report_stop_dialog";
import SendReportDialog from "../../components/send_report_dialog";
import { BoxCenterStart } from "../../style/box";
import { BaseDialog } from "../../components/base_dialog";
import { useAuth } from "../../contexts/AuthContext";
import { APPROVAL_STATUS, ROLE_UUID, REPORTING_TIME } from "../../const/config";
import {
  PrimaryInspectionStep01,
  PrimaryInspectionStep02,
  PrimaryInspectionStep03,
  PrimaryInspectionStep04,
  PrimaryInspectionStep05,
  PrimaryInspectionAfterApprovalTask,
} from "../../components/workflow_step";

import ErrorScreen from "../../components/error_screen";
import { commonStyles } from "../../style/common";
import { useInspectionLogic } from "../../hooks/use_inspection_logic";
import SkeletonLoading from "../../components/skeleton_loading";

import dayjs from 'dayjs';
import "dayjs/locale/ja";
import { PRIMARY_INSPECTION_TEXTS, PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS } from "../../const/inspection_texts";
import { postData } from "../../func/api";

dayjs.locale("ja");

const PrimaryInspection = () => {
  const { uuid } = useParams();
  const { currentUser } = useAuth();

  // State
  const {
    earthquake, setEarthquake, checkResult, approval,
    mailInfo, loading, open, setOpen, isChecked, setIsChecked,
    createComplete, setCreateComplete, checkSummary, setCheckSummary,
    mandatoryFacilitys, setMandatoryFacilitys, facilityDams, setFacilityDams,
    taskStatus, setTaskStatus, approvalStatus, isInputter,
    isReviewer, isReadOnly, isApprovedNotSent, isSent,
    isValidTask, handleRestart, reportDetailList, department
  } = useInspectionLogic(uuid, 'primary');

  // ハンドラー
  const handleNext = useCallback(async () => {
    try {
      // taskStatus が 16 または 17 の場合のみ更新処理
      if (taskStatus === 16 || taskStatus === 17) {
        const payload = {
          table_name: "report_progress",
          column_name1: taskStatus === 16 ? "pi_reported_at" : "pi_reported_to_internal_at",
          column_name2: taskStatus === 16 ? "pi_reported_by" : "pi_reported_to_internal_by",
          earthquake: earthquake,
          user_uuid: currentUser.id
        };
        await postData("api/post_update_report_progress", payload);
      }

      // taskStatus に応じた画面遷移やステータス変更
      if (taskStatus === 16) setTaskStatus(17);
      else if (taskStatus === 17) setTaskStatus(18);
      else if (taskStatus === 18 && earthquake?.uuid) {
        window.location.href = `/secondary_inspection/${earthquake.uuid}`;
      }

    } catch (err) {
      console.error("更新失敗しました。：", err);
    } finally {
      // ダイアログを閉じる
      setOpen(false);
    }
  }, [taskStatus, earthquake, currentUser]);

  const showTitle = taskStatus === 15;
  const showApproval = approvalStatus !== APPROVAL_STATUS.INIT && taskStatus === 15;

  if (loading) return <SkeletonLoading />;

  if (!isValidTask) {
    return <ErrorScreen {...{ earthquake, open, onOpen: () => setOpen(true), onClose: () => setOpen(false), onRestart: handleRestart }} />;
  }


  return (
    <BoxCenterStart sx={commonStyles.page}>
      <Grid container spacing={7} columns={12} sx={commonStyles.container}>

        {/* ヘッダー部分 */}
        {showTitle && (
          <Grid size={{ xs: 12 }}>
            <ReportStopDialog initial_report_uuid={earthquake.initial_report_uuid} earthquake_uuid={earthquake.uuid} />
            <TypographyTitle>{earthquake.name}</TypographyTitle>
          </Grid>
        )}

        {showApproval && (
          <Grid size={{ xs: 12 }}>
            <TypographyBold>承認状況</TypographyBold>
            <CommonCard child={<ApprovalStatusInfo approval={approval} approvalStatus={approvalStatus} />} />
          </Grid>
        )}

        <DamReportStepper reportDetailList={reportDetailList} maxWidth={1100} />

        {/* 入力者モード */}
        {isInputter && (
          <>
            <PrimaryInspectionStep01 title={PRIMARY_INSPECTION_TEXTS.inputter.step01.title} lead={PRIMARY_INSPECTION_TEXTS.inputter.step01.lead} descriptions={PRIMARY_INSPECTION_TEXTS.inputter.step01.descriptions} earthquake={earthquake} setEarthquake={setEarthquake} isReadOnly={isReadOnly} />
            <PrimaryInspectionStep02 title={PRIMARY_INSPECTION_TEXTS.inputter.step02.title} lead={PRIMARY_INSPECTION_TEXTS.inputter.step02.lead} descriptions={PRIMARY_INSPECTION_TEXTS.inputter.step02.descriptions} checkSummary={checkSummary} setCheckSummary={setCheckSummary} checkResult={checkResult} earthquake={earthquake} isReadOnly={isReadOnly} />
            <PrimaryInspectionStep03 title={PRIMARY_INSPECTION_TEXTS.inputter.step03.title} lead={PRIMARY_INSPECTION_TEXTS.inputter.step03.lead} descriptions={PRIMARY_INSPECTION_TEXTS.inputter.step03.descriptions} checkSummary={checkSummary} setCheckSummary={setCheckSummary} isReadOnly={isReadOnly} />
            <PrimaryInspectionStep04 title={PRIMARY_INSPECTION_TEXTS.inputter.step04.title} lead={PRIMARY_INSPECTION_TEXTS.inputter.step04.lead} descriptions={PRIMARY_INSPECTION_TEXTS.inputter.step04.descriptions} earthquake={earthquake} checkResult={checkResult} checkSummary={checkSummary} setCreateComplete={setCreateComplete} checkSummaryDataPrime={{}} isCreateExcel={true} />
            <PrimaryInspectionStep05 title={PRIMARY_INSPECTION_TEXTS.inputter.step05.title} lead={PRIMARY_INSPECTION_TEXTS.inputter.step05.lead} earthquake={earthquake} checkResult={checkResult} checkSummary={checkSummary} createComplete={createComplete} isApproveRequest />
          </>
        )}

        {/* レビュアーモード */}
        {isReviewer && (
          <>
            <PrimaryInspectionStep01 title={PRIMARY_INSPECTION_TEXTS.reviewer.step01.title} lead={PRIMARY_INSPECTION_TEXTS.reviewer.step01.lead} descriptions={PRIMARY_INSPECTION_TEXTS.reviewer.step01.descriptions} earthquake={earthquake} setEarthquake={setEarthquake} isReadOnly={isReadOnly} />
            <PrimaryInspectionStep02 title={PRIMARY_INSPECTION_TEXTS.reviewer.step02.title} lead={PRIMARY_INSPECTION_TEXTS.reviewer.step02.lead} descriptions={PRIMARY_INSPECTION_TEXTS.reviewer.step02.descriptions} checkSummary={checkSummary} setCheckSummary={setCheckSummary} checkResult={checkResult} earthquake={earthquake} isReadOnly={isReadOnly} />
            <PrimaryInspectionStep03 title={PRIMARY_INSPECTION_TEXTS.reviewer.step03.title} lead={PRIMARY_INSPECTION_TEXTS.reviewer.step03.lead} descriptions={PRIMARY_INSPECTION_TEXTS.reviewer.step03.descriptions} checkSummary={checkSummary} setCheckSummary={setCheckSummary} isReadOnly={isReadOnly} />
            <PrimaryInspectionStep04 title={PRIMARY_INSPECTION_TEXTS.reviewer.step04.title} lead={PRIMARY_INSPECTION_TEXTS.reviewer.step04.lead} earthquake={earthquake} isCreateExcel={false} />
            <PrimaryInspectionStep05 title={PRIMARY_INSPECTION_TEXTS.reviewer.step05.title} lead={PRIMARY_INSPECTION_TEXTS.reviewer.step05.lead} descriptions={PRIMARY_INSPECTION_TEXTS.reviewer.step05.descriptions} earthquake={earthquake} isApproveRequest={false} />
          </>
        )}

        {/* メール確認・送信 */}
        {isApprovedNotSent && (
          <>
            <Grid size={{ xs: 12 }}><MailConfirm isChecked={isChecked} setIsChecked={setIsChecked} mail_info={mailInfo} department={department} /></Grid>
            <Grid size={{ xs: 12 }}><SendReportDialog uuid={earthquake.primary_inspection_uuid} earthquake_uuid={earthquake.uuid} taskStatus={taskStatus} setTaskStatus={setTaskStatus} disabled={!isChecked} type="primary" department={department} /></Grid>
          </>
        )}

        {/* 送信後タスク */}
        {isSent && taskStatus === 16 && <PrimaryInspectionAfterApprovalTask title={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status16.title} lead={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status16.lead} appealContent={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status16.appealContent} onClickRequestButton={() => setOpen(true)} />}
        {isSent && taskStatus === 17 &&
          <PrimaryInspectionAfterApprovalTask
            title={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status17.title}
            appealContent={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status17.appealContent}
            lead={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status17.lead}
            listItems={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status17.listItems}
            onClickRequestButton={() => setOpen(true)} />}
        {isSent && taskStatus === 18 && <PrimaryInspectionAfterApprovalTask title={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status18.title} lead={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status18.lead} buttonContent={PRIMARY_INSPECTION_POST_SEND_TASKS_TEXTS.status18.buttonContent} onClickRequestButton={() => setOpen(true)} />}

        <BaseDialog open={open} onClose={() => setOpen(false)} title="業務実施確認" message={`次の業務を実施します。\nよろしいですか？`} onConfirm={handleNext} confirmText="OK" cancelText="キャンセル" />
        <ReportCountdown occued_at={earthquake.occured_at} addedMinutes={REPORTING_TIME.PRIMARYCHECK} />
      </Grid>
    </BoxCenterStart>
  );
};

export default PrimaryInspection;